import React, { createContext, useContext, useState, ReactNode } from "react";
import type { DirectMessageRecipient } from "@/components/DirectProgramMessageDrawer";
import type { PabDirectMessageRecipient } from "@/components/DirectPabMessageDrawer";

type DrawerHandler = (() => void) | null;

type AdvisorGoalsDrawerTab = "goals" | "sessions" | "messages";

interface DrawerContextType {
  openDrawer: () => void;
  openDrawerWithMessages: () => void;
  openDrawerWithSessions: () => void;
  openDrawerWithGoals?: () => void;
  setDrawerHandler: (handler: DrawerHandler) => void;
  setMessagesDrawerHandler: (handler: DrawerHandler) => void;
  setSessionsDrawerHandler: (handler: DrawerHandler) => void;
  setGoalsDrawerHandler: (handler: DrawerHandler) => void;
  // MessagesListDrawer — inbox/overview, opened from header icon
  isMessagesListDrawerOpen: boolean;
  openMessagesListDrawer: () => void;
  closeMessagesListDrawer: () => void;
  // MessagesDrawer (group/1:1) - opened from list drawer
  isMessagesDrawerOpen: boolean;
  // v3: advisor identity is the user_id (SHA256 string), not an int PK.
  messagesDrawerSelectedAdvisorId: string | null;
  messagesDrawerSessionId: number | null;
  messagesDrawerProgramExternalId: string | null;
  messagesDrawerChatTitle: string | null;
  messagesDrawerChatSubtitle: string | null;
  /** True when the opener asked for a PAB's group thread rather than the
   *  advisor's default 1:1 with the achiever — see MessagesDrawer. */
  messagesDrawerGroupThread: boolean;
  openMessagesDrawer: (
    advisorId?: string,
    sessionId?: number,
    programExternalId?: string,
    chatTitle?: string,
    chatSubtitle?: string,
    options?: { groupThread?: boolean },
  ) => void;
  closeMessagesDrawer: () => void;
  // DirectProgramMessageDrawer — global 1:1 DM for programs
  isDirectProgramMessageDrawerOpen: boolean;
  directProgramMessageDrawerProgramId: number | null;
  directProgramMessageDrawerProgramName: string | null;
  directProgramMessageDrawerRecipients: DirectMessageRecipient[];
  openDirectProgramMessageDrawer: (programId: number, programName: string, recipients: DirectMessageRecipient[]) => void;
  closeDirectProgramMessageDrawer: () => void;
  // DirectPabMessageDrawer — global 1:1 DM for PAB session members
  isDirectPabMessageDrawerOpen: boolean;
  directPabMessageDrawerSessionId: number | null;
  directPabMessageDrawerSessionLabel: string | null;
  directPabMessageDrawerRecipients: PabDirectMessageRecipient[];
  /** Text the composer opens with, for the sender to edit before sending. */
  directPabMessageDrawerInitialMessage: string | null;
  openDirectPabMessageDrawer: (
    sessionId: number,
    sessionLabel: string,
    recipients: PabDirectMessageRecipient[],
    initialMessage?: string,
  ) => void;
  closeDirectPabMessageDrawer: () => void;
  // MultiHubDrawer state management
  isMultiHubDrawerOpen: boolean;
  multiHubDrawerDefaultTab: "sessions" | "messages";
  multiHubDrawerSelectedAdvisorId: string | null;
  openMultiHubDrawer: (tab?: "sessions" | "messages") => void;
  closeMultiHubDrawer: () => void;
  setSelectedAdvisorForMessages: (advisorId: string | null) => void;
  // AdvisorGoalsDrawer state management
  isAdvisorGoalsDrawerOpen: boolean;
  advisorGoalsDrawerDefaultTab: AdvisorGoalsDrawerTab;
  advisorGoalsDrawerSelectedPabId: number | null;
  openAdvisorGoalsDrawer: (tab?: AdvisorGoalsDrawerTab, pabSessionId?: number) => void;
  closeAdvisorGoalsDrawer: () => void;
  setAdvisorGoalsDrawerSelectedPabId: (pabSessionId: number | null) => void;
  // SessionPrepDrawer (advisor read-only view) — opened from notifications
  isSessionPrepDrawerOpen: boolean;
  sessionPrepDrawerMeetingId: number | null;
  openSessionPrepDrawer: (meetingId: number | null) => void;
  closeSessionPrepDrawer: () => void;
  // Achiever Session Prep / Session Summary drawers — the same drawers the
  // journey Sessions tab opens, hoisted to the layout so a notification can
  // open them from any route. programId is the meeting's own program (PAB or
  // Expert), which is not necessarily the achiever's "active" PAB.
  isAchieverSessionPrepDrawerOpen: boolean;
  achieverSessionPrepDrawerMeetingId: number | null;
  achieverSessionPrepDrawerProgramId: number | null;
  openAchieverSessionPrepDrawer: (meetingId: number, programId?: number | null) => void;
  closeAchieverSessionPrepDrawer: () => void;
  isAchieverSessionSummaryDrawerOpen: boolean;
  achieverSessionSummaryDrawerMeetingId: number | null;
  achieverSessionSummaryDrawerProgramId: number | null;
  openAchieverSessionSummaryDrawer: (meetingId: number, programId?: number | null) => void;
  closeAchieverSessionSummaryDrawer: () => void;
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

interface DrawerProviderProps {
  children: ReactNode;
}

export const DrawerProvider: React.FC<DrawerProviderProps> = ({ children }) => {
  const [drawerHandler, setDrawerHandlerState] = useState<DrawerHandler>(null);
  const [messagesDrawerHandler, setMessagesDrawerHandlerState] = useState<DrawerHandler>(
    null,
  );
  const [sessionsDrawerHandler, setSessionsDrawerHandlerState] = useState<DrawerHandler>(
    null,
  );
  const [goalsDrawerHandler, setGoalsDrawerHandlerState] = useState<DrawerHandler>(null);

  // MessagesListDrawer — inbox/overview opened from header icon
  const [isMessagesListDrawerOpen, setIsMessagesListDrawerOpen] = useState(false);
  const openMessagesListDrawer = () => setIsMessagesListDrawerOpen(true);
  const closeMessagesListDrawer = () => setIsMessagesListDrawerOpen(false);

  // MessagesDrawer (group/1:1) state - opened from list drawer
  const [isMessagesDrawerOpen, setIsMessagesDrawerOpen] = useState(false);
  // v3: advisor identity is the user_id (SHA256 string), not an int PK.
  const [messagesDrawerSelectedAdvisorId, setMessagesDrawerSelectedAdvisorId] = useState<
    string | null
  >(null);
  const [messagesDrawerSessionId, setMessagesDrawerSessionId] = useState<number | null>(null);
  const [messagesDrawerProgramExternalId, setMessagesDrawerProgramExternalId] = useState<
    string | null
  >(null);
  const [messagesDrawerChatTitle, setMessagesDrawerChatTitle] = useState<string | null>(null);
  const [messagesDrawerChatSubtitle, setMessagesDrawerChatSubtitle] = useState<string | null>(null);
  const [messagesDrawerGroupThread, setMessagesDrawerGroupThread] = useState(false);
  const openMessagesDrawer = (
    advisorId?: string,
    sessionId?: number,
    programExternalId?: string,
    chatTitle?: string,
    chatSubtitle?: string,
    options?: { groupThread?: boolean },
  ) => {
    setMessagesDrawerSelectedAdvisorId(advisorId ?? null);
    setMessagesDrawerGroupThread(!!options?.groupThread);
    setMessagesDrawerSessionId(sessionId ?? null);
    setMessagesDrawerProgramExternalId(programExternalId ?? null);
    setMessagesDrawerChatTitle(chatTitle ?? null);
    setMessagesDrawerChatSubtitle(chatSubtitle ?? null);
    setIsMessagesDrawerOpen(true);
  };
  const closeMessagesDrawer = () => {
    setIsMessagesDrawerOpen(false);
    setMessagesDrawerSelectedAdvisorId(null);
    setMessagesDrawerSessionId(null);
    setMessagesDrawerProgramExternalId(null);
    setMessagesDrawerChatTitle(null);
    setMessagesDrawerChatSubtitle(null);
    setMessagesDrawerGroupThread(false);
  };

  // DirectProgramMessageDrawer — global 1:1 DM for programs
  const [isDirectProgramMessageDrawerOpen, setIsDirectProgramMessageDrawerOpen] = useState(false);
  const [directProgramMessageDrawerProgramId, setDirectProgramMessageDrawerProgramId] = useState<number | null>(null);
  const [directProgramMessageDrawerProgramName, setDirectProgramMessageDrawerProgramName] = useState<string | null>(null);
  const [directProgramMessageDrawerRecipients, setDirectProgramMessageDrawerRecipients] = useState<import("@/components/DirectProgramMessageDrawer").DirectMessageRecipient[]>([]);
  const openDirectProgramMessageDrawer = (programId: number, programName: string, recipients: import("@/components/DirectProgramMessageDrawer").DirectMessageRecipient[]) => {
    setDirectProgramMessageDrawerProgramId(programId);
    setDirectProgramMessageDrawerProgramName(programName);
    setDirectProgramMessageDrawerRecipients(recipients);
    setIsDirectProgramMessageDrawerOpen(true);
  };
  const closeDirectProgramMessageDrawer = () => {
    setIsDirectProgramMessageDrawerOpen(false);
    setDirectProgramMessageDrawerProgramId(null);
    setDirectProgramMessageDrawerProgramName(null);
    setDirectProgramMessageDrawerRecipients([]);
  };

  // DirectPabMessageDrawer — global 1:1 DM for PAB session members
  const [isDirectPabMessageDrawerOpen, setIsDirectPabMessageDrawerOpen] = useState(false);
  const [directPabMessageDrawerSessionId, setDirectPabMessageDrawerSessionId] = useState<number | null>(null);
  const [directPabMessageDrawerSessionLabel, setDirectPabMessageDrawerSessionLabel] = useState<string | null>(null);
  const [directPabMessageDrawerRecipients, setDirectPabMessageDrawerRecipients] = useState<PabDirectMessageRecipient[]>([]);
  const [directPabMessageDrawerInitialMessage, setDirectPabMessageDrawerInitialMessage] = useState<string | null>(null);
  const openDirectPabMessageDrawer = (
    sessionId: number,
    sessionLabel: string,
    recipients: PabDirectMessageRecipient[],
    initialMessage?: string,
  ) => {
    setDirectPabMessageDrawerSessionId(sessionId);
    setDirectPabMessageDrawerSessionLabel(sessionLabel);
    setDirectPabMessageDrawerRecipients(recipients);
    setDirectPabMessageDrawerInitialMessage(initialMessage ?? null);
    setIsDirectPabMessageDrawerOpen(true);
  };
  const closeDirectPabMessageDrawer = () => {
    setIsDirectPabMessageDrawerOpen(false);
    setDirectPabMessageDrawerSessionId(null);
    setDirectPabMessageDrawerSessionLabel(null);
    setDirectPabMessageDrawerRecipients([]);
    setDirectPabMessageDrawerInitialMessage(null);
  };

  // MultiHubDrawer state
  const [isMultiHubDrawerOpen, setIsMultiHubDrawerOpen] = useState(false);
  const [multiHubDrawerDefaultTab, setMultiHubDrawerDefaultTab] = useState<
    "sessions" | "messages"
  >("messages");
  // v3: advisor identity is the user_id (SHA256 string), not an int PK.
  const [multiHubDrawerSelectedAdvisorId, setMultiHubDrawerSelectedAdvisorId] = useState<
    string | null
  >(null);

  const openMultiHubDrawer = (tab: "sessions" | "messages" = "messages") => {
    setMultiHubDrawerDefaultTab(tab);
    setIsMultiHubDrawerOpen(true);
  };

  const closeMultiHubDrawer = () => {
    setIsMultiHubDrawerOpen(false);
    setMultiHubDrawerSelectedAdvisorId(null);
  };

  const setSelectedAdvisorForMessages = (advisorId: string | null) => {
    setMultiHubDrawerSelectedAdvisorId(advisorId);
  };

  // AdvisorGoalsDrawer state
  const [isAdvisorGoalsDrawerOpen, setIsAdvisorGoalsDrawerOpen] = useState(false);
  const [advisorGoalsDrawerDefaultTab, setAdvisorGoalsDrawerDefaultTab] = useState<
    AdvisorGoalsDrawerTab
  >("messages");
  const [advisorGoalsDrawerSelectedPabId, setAdvisorGoalsDrawerSelectedPabId] = useState<
    number | null
  >(null);

  const openAdvisorGoalsDrawer = (
    tab: AdvisorGoalsDrawerTab = "messages",
    pabSessionId?: number,
  ) => {
    setAdvisorGoalsDrawerDefaultTab(tab);
    if (pabSessionId !== undefined) {
      setAdvisorGoalsDrawerSelectedPabId(pabSessionId);
    }
    setIsAdvisorGoalsDrawerOpen(true);
  };

  const closeAdvisorGoalsDrawer = () => {
    setIsAdvisorGoalsDrawerOpen(false);
    setAdvisorGoalsDrawerSelectedPabId(null);
  };

  // SessionPrepDrawer (advisor read-only view) — opened from notifications
  const [isSessionPrepDrawerOpen, setIsSessionPrepDrawerOpen] = useState(false);
  const [sessionPrepDrawerMeetingId, setSessionPrepDrawerMeetingId] = useState<number | null>(
    null,
  );
  const openSessionPrepDrawer = (meetingId: number | null) => {
    setSessionPrepDrawerMeetingId(meetingId);
    setIsSessionPrepDrawerOpen(true);
  };
  const closeSessionPrepDrawer = () => {
    setIsSessionPrepDrawerOpen(false);
    setSessionPrepDrawerMeetingId(null);
  };

  // Achiever Session Prep / Session Summary drawers — opened from notifications
  const [isAchieverSessionPrepDrawerOpen, setIsAchieverSessionPrepDrawerOpen] = useState(false);
  const [achieverSessionPrepDrawerMeetingId, setAchieverSessionPrepDrawerMeetingId] = useState<
    number | null
  >(null);
  const [achieverSessionPrepDrawerProgramId, setAchieverSessionPrepDrawerProgramId] = useState<
    number | null
  >(null);
  const openAchieverSessionPrepDrawer = (meetingId: number, programId?: number | null) => {
    setAchieverSessionPrepDrawerMeetingId(meetingId);
    setAchieverSessionPrepDrawerProgramId(programId ?? null);
    setIsAchieverSessionPrepDrawerOpen(true);
  };
  const closeAchieverSessionPrepDrawer = () => {
    setIsAchieverSessionPrepDrawerOpen(false);
    setAchieverSessionPrepDrawerMeetingId(null);
    setAchieverSessionPrepDrawerProgramId(null);
  };

  const [isAchieverSessionSummaryDrawerOpen, setIsAchieverSessionSummaryDrawerOpen] =
    useState(false);
  const [achieverSessionSummaryDrawerMeetingId, setAchieverSessionSummaryDrawerMeetingId] =
    useState<number | null>(null);
  const [achieverSessionSummaryDrawerProgramId, setAchieverSessionSummaryDrawerProgramId] =
    useState<number | null>(null);
  const openAchieverSessionSummaryDrawer = (meetingId: number, programId?: number | null) => {
    setAchieverSessionSummaryDrawerMeetingId(meetingId);
    setAchieverSessionSummaryDrawerProgramId(programId ?? null);
    setIsAchieverSessionSummaryDrawerOpen(true);
  };
  const closeAchieverSessionSummaryDrawer = () => {
    setIsAchieverSessionSummaryDrawerOpen(false);
    setAchieverSessionSummaryDrawerMeetingId(null);
    setAchieverSessionSummaryDrawerProgramId(null);
  };

  const openDrawer = () => {
    if (drawerHandler) {
      drawerHandler();
    }
  };

  const openDrawerWithSessions = () => {
    if (sessionsDrawerHandler) {
      sessionsDrawerHandler();
    } else {
      // Fallback to MultiHubDrawer if no handler is set
      openMultiHubDrawer("sessions");
    }
  };

  // Open MessagesListDrawer when header Messages icon is clicked
  const openDrawerWithMessages = () => {
    if (messagesDrawerHandler) {
      messagesDrawerHandler();
    } else {
      openMessagesListDrawer();
    }
  };

  const openDrawerWithGoals = () => {
    if (goalsDrawerHandler) {
      goalsDrawerHandler();
    } else {
      // For advisor flow, open AdvisorGoalsDrawer; for achiever, fallback to MultiHubDrawer
      openAdvisorGoalsDrawer("goals");
    }
  };

  const setDrawerHandler = (handler: DrawerHandler) => {
    setDrawerHandlerState(() => handler);
  };

  const setMessagesDrawerHandler = (handler: DrawerHandler) => {
    setMessagesDrawerHandlerState(() => handler);
  };

  const setSessionsDrawerHandler = (handler: DrawerHandler) => {
    setSessionsDrawerHandlerState(() => handler);
  };

  const setGoalsDrawerHandler = (handler: DrawerHandler) => {
    setGoalsDrawerHandlerState(() => handler);
  };

  return (
    <DrawerContext.Provider
      value={{
        openDrawer,
        openDrawerWithMessages,
        openDrawerWithSessions,
        openDrawerWithGoals,
        setDrawerHandler,
        setMessagesDrawerHandler,
        setSessionsDrawerHandler,
        setGoalsDrawerHandler,
        // MessagesListDrawer state
        isMessagesListDrawerOpen,
        openMessagesListDrawer,
        closeMessagesListDrawer,
        // MessagesDrawer state
        isMessagesDrawerOpen,
        messagesDrawerSelectedAdvisorId,
        messagesDrawerSessionId,
        messagesDrawerProgramExternalId,
        messagesDrawerChatTitle,
        messagesDrawerGroupThread,
        messagesDrawerChatSubtitle,
        openMessagesDrawer,
        closeMessagesDrawer,
        // DirectProgramMessageDrawer state
        isDirectProgramMessageDrawerOpen,
        directProgramMessageDrawerProgramId,
        directProgramMessageDrawerProgramName,
        directProgramMessageDrawerRecipients,
        openDirectProgramMessageDrawer,
        closeDirectProgramMessageDrawer,
        // DirectPabMessageDrawer state
        isDirectPabMessageDrawerOpen,
        directPabMessageDrawerSessionId,
        directPabMessageDrawerSessionLabel,
        directPabMessageDrawerRecipients,
        directPabMessageDrawerInitialMessage,
        openDirectPabMessageDrawer,
        closeDirectPabMessageDrawer,
        // MultiHubDrawer state
        isMultiHubDrawerOpen,
        multiHubDrawerDefaultTab,
        multiHubDrawerSelectedAdvisorId,
        openMultiHubDrawer,
        closeMultiHubDrawer,
        setSelectedAdvisorForMessages,
        // AdvisorGoalsDrawer state
        isAdvisorGoalsDrawerOpen,
        advisorGoalsDrawerDefaultTab,
        advisorGoalsDrawerSelectedPabId,
        openAdvisorGoalsDrawer,
        closeAdvisorGoalsDrawer,
        setAdvisorGoalsDrawerSelectedPabId,
        // SessionPrepDrawer state
        isSessionPrepDrawerOpen,
        sessionPrepDrawerMeetingId,
        openSessionPrepDrawer,
        closeSessionPrepDrawer,
        // Achiever Session Prep / Session Summary drawer state
        isAchieverSessionPrepDrawerOpen,
        achieverSessionPrepDrawerMeetingId,
        achieverSessionPrepDrawerProgramId,
        openAchieverSessionPrepDrawer,
        closeAchieverSessionPrepDrawer,
        isAchieverSessionSummaryDrawerOpen,
        achieverSessionSummaryDrawerMeetingId,
        achieverSessionSummaryDrawerProgramId,
        openAchieverSessionSummaryDrawer,
        closeAchieverSessionSummaryDrawer,
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
};

// Export useDrawer hook separately to avoid fast refresh issues
const useDrawer = (): DrawerContextType => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error("useDrawer must be used within a DrawerProvider");
  }
  return context;
};

export { useDrawer };


